import { useState } from 'react';
import { PurchaseHistory, User } from '@/types/user';
import { fetchPurchaseHistory, addPurchaseToSupabase, migrateLocalPurchaseHistory } from '@/utils/supabase/purchaseUtils';
import { fetchPurchaseHistory as fetchUserPurchaseHistory } from '@/utils/supabase/purchase/fetchPurchaseHistory';
import { toast } from '@/hooks/use-toast';

export const usePurchaseHistory = (user: User | null) => {
  const [purchaseHistory, setPurchaseHistory] = useState<PurchaseHistory[]>([]);

  const loadPurchaseHistory = async (userId: string) => {
    try {
      // Use the function to get detailed purchase history
      const history = await fetchUserPurchaseHistory(userId);
      
      // Transform PurchaseHistoryItem[] to PurchaseHistory[]
      const transformedHistory: PurchaseHistory[] = history.map(item => ({
        id: item.id,
        userId: userId, // Add the missing userId property
        items: item.items.map(historyItem => ({
          id: historyItem.productId,
          name: historyItem.name,
          price: historyItem.price,
          quantity: historyItem.quantity
        })),
        total: item.total,
        date: new Date(item.date),
        paymentMethod: item.paymentMethod,
        promoCode: item.promoCode,
        promoDiscount: item.promoDiscount,
        creditApplied: item.creditApplied,
        shippingCost: item.shippingCost,
        subtotal: item.subtotal,
        taxAmount: item.taxAmount,
        promoEligibleItems: item.promoEligibleItems
      }));
      
      setPurchaseHistory(transformedHistory);
    } catch (error) {
      console.error('Error loading purchase history:', error);
    }
  };

  const addPurchaseToHistory = async (
    purchase: Omit<PurchaseHistory, 'id' | 'userId' | 'date'>,
    metadata: any = {} // Make metadata optional with default empty object
  ) => {
    if (!user) return;
    
    try {
      const success = await addPurchaseToSupabase(
        user.id,
        purchase.items,
        purchase.total,
        purchase.paymentMethod,
        metadata
      );

      if (success) {
        const updatedHistory = await fetchPurchaseHistory(user.id);
        setPurchaseHistory(updatedHistory);
      }
    } catch (error) {
      console.error('Error adding purchase to history:', error);
      toast({
        title: 'Error',
        description: 'Failed to save purchase history',
        variant: 'destructive',
      });
    }
  };

  const migrateHistory = async (userId: string, localHistory: PurchaseHistory[]) => {
    try {
      const migrated = await migrateLocalPurchaseHistory(userId, localHistory);
      if (migrated) {
        const updatedHistory = await fetchPurchaseHistory(userId);
        setPurchaseHistory(updatedHistory);
        localStorage.removeItem('localPurchaseHistory');
      }
    } catch (error) {
      console.error('Error migrating purchase history:', error);
    }
  };

  return {
    purchaseHistory,
    setPurchaseHistory,
    loadPurchaseHistory,
    addPurchaseToHistory,
    migrateHistory,
  };
};
